import React, { useState, useRef, useEffect } from 'react';
import { API_BASE } from '../config';

function AIStrologer({ chartData, mahadasha, antardasha, onClose, onRequireSignup }) {
  const mahadashaLord = mahadasha?.lord || 'Unknown';
  const antardashaLord = antardasha?.lord || 'Unknown';
  const adStartDate = antardasha?.startDate || '';
  const adEndDate = antardasha?.endDate || '';

  const [messages, setMessages] = useState([
    {
      role: 'system',
      content: `🪔 Namaste, I am AI-STROLOGER, your honest Vedic astrology guide.\n\nI'm here to discuss your **${mahadashaLord}/${antardashaLord} period** (${formatDate(adStartDate)} → ${formatDate(adEndDate)}).\n\nI'll provide insights from:\n📚 Classical Vedic texts (Brihat Parasara, Phala Deepika, Lal Kitab)\n🌐 Modern Vedic sources\n💯 Honest acknowledgment when sources are limited\n\nUnlike traditional jyotishis who may stretch the truth, I'll always tell you what I know and don't know — and help you reflect like a therapist when needed.\n\nWhat would you like to know about this period?`
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [gated, setGated] = useState(null); // { error, signup_url, used, limit } when 401
  const [quota, setQuota] = useState(null); // { type, remaining, limit } from last response
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const suggestedQuestions = [
    `What did/does ${mahadashaLord}/${antardashaLord} mean for my life?`,
    `What career changes happen during this period?`,
    `What relationships should I expect?`,
    `Are there health concerns to watch?`,
    `What spiritual lessons does this bring?`,
    `What remedies are recommended?`
  ];

  const handleSubmit = async (questionText) => {
    const question = questionText || input.trim();
    if (!question || loading) return;

    const userMessage = { role: 'user', content: question };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    // Send only essential chart data (not the full 270KB payload)
    const essentialChartData = {
      ascendant: chartData?.ascendant || chartData?.houses?.ascendant,
      houses: { ascendant: chartData?.houses?.ascendant || chartData?.ascendant },
      planets: chartData?.planets ? Object.fromEntries(
        Object.entries(chartData.planets).map(([k, v]) => [k, {
          sign: v?.sign,
          signIndex: v?.signIndex,
          degree: v?.degree,
          minute: v?.minute,
          nakshatra: v?.nakshatra,
          isRetrograde: v?.isRetrograde
        }])
      ) : {},
      ayanamsha: chartData?.ayanamsha
    };

    try {
      // Send API key if user is signed in (gives them higher per-tier quota)
      const apiKey = localStorage.getItem('astrogem_api_key');
      const headers = { 'Content-Type': 'application/json' };
      if (apiKey) headers['x-api-key'] = apiKey;

      const response = await fetch(`${API_BASE}/api/aistrologer`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          chartData: essentialChartData,
          mahadasha: mahadashaLord,
          antardasha: antardashaLord,
          mahadashaStart: mahadasha?.startDate,
          mahadashaEnd: mahadasha?.endDate,
          antardashaStart: adStartDate,
          antardashaEnd: adEndDate,
          isPast: adEndDate ? new Date(adEndDate) < new Date() : false,
          isCurrent: adStartDate && adEndDate ? new Date() >= new Date(adStartDate) && new Date() < new Date(adEndDate) : false,
          question
        })
      });

      const data = await response.json().catch(() => ({}));

      // GATED — usage limit hit (anonymous or paid).
      // Backend returns { gated: true, error, signup_url?, upgrade_url?, used, limit }.
      if (response.status === 401 || response.status === 429 || data.gated) {
        setGated({
          message: data.error || 'You\'ve hit your AI question limit.',
          signup_url: data.signup_url,
          upgrade_url: data.upgrade_url,
          used: data.used,
          limit: data.limit,
          reason: data.reason || (response.status === 429 ? 'quota_exceeded' : 'auth_required'),
        });
        // Add a friendly message in the chat too
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.error || 'Please sign up to continue chatting.',
          gated: true,
        }]);
        return;
      }

      if (!response.ok || data.error) {
        throw new Error(data.error || `Server returned ${response.status}`);
      }

      setQuota(data.quota || null);

      const assistantMessage = {
        role: 'assistant',
        content: data.answer || 'I apologize, but I could not generate a response. Please try rephrasing your question.',
        sources: data.sources || [],
        therapistMode: data.therapistMode || false
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('AI-Strologer error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `⚠️ Error: ${error.message || 'Unable to connect to AI-Strologer'}. Please try again in a moment.`,
        error: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-purple-500/30 rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-900 to-indigo-900 border-b border-purple-500/30 p-4 rounded-t-lg flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="text-2xl">🪔</div>
              <div>
                <h2 className="text-lg font-bold text-white">AI-STROLOGER</h2>
                <p className="text-purple-200 text-xs">
                  {mahadashaLord} Mahadasha · {antardashaLord} Antardasha
                </p>
              </div>
            </div>
            <p className="text-white/60 text-[10px] mt-1 font-mono">
              {formatDate(adStartDate)} → {formatDate(adEndDate)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white text-2xl leading-none p-2"
          >
            ×
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.map((msg, idx) => (
            <div key={idx}>
              {msg.role === 'system' && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-amber-100 text-sm whitespace-pre-line">
                  {msg.content}
                </div>
              )}
              {msg.role === 'user' && (
                <div className="flex justify-end">
                  <div className="bg-purple-600 text-white rounded-lg px-4 py-2 max-w-[80%] text-sm">
                    {msg.content}
                  </div>
                </div>
              )}
              {msg.role === 'assistant' && (
                <div className="flex flex-col items-start">
                  <div className={`rounded-lg px-4 py-3 max-w-[90%] text-sm ${
                    msg.error
                      ? 'bg-red-500/20 border border-red-500/40 text-red-200'
                      : msg.therapistMode
                        ? 'bg-blue-500/10 border border-blue-500/30 text-blue-100'
                        : 'bg-slate-800 border border-slate-700 text-white'
                  }`}>
                    <div className="flex items-center gap-2 mb-2 text-xs font-semibold">
                      <span>🪔 AI-Strologer</span>
                      {msg.therapistMode && (
                        <span className="bg-blue-500/30 px-2 py-0.5 rounded text-[10px]">Honest Mode</span>
                      )}
                    </div>
                    <FormattedResponse content={msg.content} />

                    {msg.sources && msg.sources.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-600">
                        <p className="text-[10px] uppercase tracking-wider font-semibold text-purple-300 mb-1">📚 Sources Cited</p>
                        <div className="space-y-1">
                          {msg.sources.map((src, i) => (
                            <div key={i} className="text-[10px] bg-purple-900/30 rounded px-2 py-1">
                              <span className="text-purple-300 font-semibold">{src.type}:</span>{' '}
                              <span className="text-white/80">{src.reference}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
          {loading && (
            <div className="flex items-center gap-2 text-white/60 text-sm">
              <div className="animate-pulse">🪔</div>
              <span>AI-Strologer is consulting the texts...</span>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Suggested Questions */}
        {messages.length === 1 && (
          <div className="px-4 pb-2">
            <p className="text-white/50 text-xs mb-2">💡 Try asking:</p>
            <div className="flex flex-wrap gap-2">
              {suggestedQuestions.map((q, i) => (
                <button
                  key={i}
                  onClick={() => handleSubmit(q)}
                  className="text-xs bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-full px-3 py-1 text-white/80 hover:text-white transition"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input OR signup/upgrade CTA when gated */}
        <div className="border-t border-slate-700 p-4">
          {gated ? (
            <div className="bg-gradient-to-r from-purple-900/40 to-pink-900/40 border border-purple-500/40 rounded-lg p-4 text-center">
              <div className="text-2xl mb-2">🪔</div>
              <p className="text-white font-semibold mb-1">
                {gated.reason === 'quota_exceeded' ? 'Monthly limit reached' : 'Sign up to continue your reading'}
              </p>
              <p className="text-white/70 text-sm mb-3">{gated.message}</p>
              {gated.signup_url && (
                <button
                  onClick={() => onRequireSignup ? onRequireSignup() : window.location.assign(gated.signup_url)}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 py-2 rounded-lg font-bold text-sm transition"
                >
                  Sign up free — 3 questions/month
                </button>
              )}
              {gated.upgrade_url && !gated.signup_url && (
                <button
                  onClick={() => window.location.assign(gated.upgrade_url)}
                  className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white px-6 py-2 rounded-lg font-bold text-sm transition"
                >
                  Upgrade to Pro — 200/month
                </button>
              )}
            </div>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={`Ask about your ${mahadashaLord}/${antardashaLord} period...`}
                className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white placeholder-white/40 text-sm focus:outline-none focus:border-purple-500"
                disabled={loading}
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 py-2 rounded-lg font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                Ask
              </button>
            </form>
          )}
          <div className="flex items-center justify-between mt-2">
            <p className="text-white/30 text-[10px]">
              🔮 Powered by classical Vedic texts + AI · Always honest about sources
            </p>
            {quota && quota.type !== 'unlimited' && (
              <p className="text-white/40 text-[10px]">
                {quota.remaining} of {quota.limit} questions left{quota.type === 'anonymous' ? ' before signup' : ' this month'}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function formatDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Minimal AI-response formatter. The DeepSeek system prompt outputs
 *   **Heading**
 *   paragraph text...
 *
 *   **Next Heading**
 *   ...
 *
 *   ॐ
 *
 * We render each `**X**`-only line as a small section header (purple,
 * uppercase tracking) and group following lines into a paragraph until
 * the next heading or blank line. The trailing ॐ is centered, golden,
 * and treated as a closing flourish.
 */
function FormattedResponse({ content }) {
  if (!content) return null;
  const lines = String(content).split(/\r?\n/);
  const blocks = []; // [{ type: 'heading'|'para'|'om', text }]
  let para = [];
  const flushPara = () => {
    if (para.length) {
      blocks.push({ type: 'para', text: para.join(' ').trim() });
      para = [];
    }
  };

  for (let raw of lines) {
    const line = raw.trim();
    if (!line) { flushPara(); continue; }
    // Trailing Om as a special closing flourish
    if (line === 'ॐ' || line === '🕉' || line === 'Om' || line === 'OM') {
      flushPara();
      blocks.push({ type: 'om', text: 'ॐ' });
      continue;
    }
    // Heading: a line that's entirely a bold span like **Period overview**
    const headingMatch = line.match(/^\*\*(.+?)\*\*[:：]?\s*$/);
    if (headingMatch) {
      flushPara();
      blocks.push({ type: 'heading', text: headingMatch[1] });
      continue;
    }
    // Inline bold within paragraphs: convert **X** → markers we render below
    para.push(line);
  }
  flushPara();

  const renderInline = (text) =>
    text.split(/(\*\*[^*]+\*\*)/g).map((chunk, i) =>
      chunk.startsWith('**') && chunk.endsWith('**')
        ? <strong key={i} className="text-white">{chunk.slice(2, -2)}</strong>
        : <span key={i}>{chunk}</span>
    );

  return (
    <div className="space-y-3">
      {blocks.map((b, i) => {
        if (b.type === 'heading') {
          return (
            <div key={i} className="text-purple-300 text-[11px] font-bold uppercase tracking-wider pt-1">
              {b.text}
            </div>
          );
        }
        if (b.type === 'om') {
          return (
            <div key={i} className="text-center text-amber-300 text-2xl pt-2" aria-label="Om — closing blessing">
              {b.text}
            </div>
          );
        }
        return (
          <p key={i} className="text-white/90 leading-relaxed">
            {renderInline(b.text)}
          </p>
        );
      })}
    </div>
  );
}

export default AIStrologer;
